import type { Experience, Project, Skill, PersonalInfo } from '../types';

export const personalInfo: PersonalInfo = {
  name: "Nizar Almshantaf",
  title: "Senior Software Engineer",
  email: "nizar.almshantaf@gmail.com",
  phone: "+971561066821",
  location: "Dubai, United Arab Emirates",
  linkedin: "https://ae.linkedin.com/in/nizar-almshantaf-78a0b0203",
  github: "https://github.com/nizaralmshantaf",
  summary: "Results-driven software engineer with 6+ years of experience in backend development, AI applications, and technical leadership. Proven track record in delivering scalable .NET-based solutions for government clients.",
  bio: `Adept at leading cross-functional teams, managing client relationships, and driving innovation through real-world AI use cases. 
  
  Specialized in .NET, Azure, clean architecture and microservices, with hands-on experience in AI-driven systems. Proven track record in delivering scalable cloud-native solutions for high-impact government and enterprise projects.
  
  Adept at leading cross-functional teams, managing client relationships, and driving innovation through real-world AI use cases.`
};

export const experiences: Experience[] = [
  {
    id: "rta-2025",
    company: "Roads & Transport Authority",
    position: "Data Scientist",
    location: "Dubai, United Arab Emirates",
    startDate: "2025-01",
    endDate: "Present",
    description: [
      "AI-powered Occupancy and Demand Prediction",
      "AI-driven Customer Relationship (CR) Management",
      "Voice AI Agent for Trip Planning",
      "Additional AI applications in mobility and transportation"
    ],
    technologies: ["AI", ".NET", "Azure", "Clean Architecture", "Microservices"],
    companyLogo: "/images/rta-logo.png"
  },
  {
    id: "uae-digital-lab-2021",
    company: "UAE Digital Lab",
    position: "Senior Software Engineer",
    location: "Dubai, United Arab Emirates",
    startDate: "2021-07",
    endDate: "2023-12",
    description: [
      "Led backend teams in delivering scalable .NET-based solutions for government clients",
      "Acted as primary technical contact with clients to gather requirements and present solutions",
      "Designed and enforced clean architecture and microservice patterns",
      "Oversaw end-to-end system implementation, deployment, and maintenance",
      "Mentored junior developers and conducted code reviews to ensure quality standards"
    ],
    technologies: ["Led backend teams", ".NET", "Azure", "Clean Architecture", "Microservices"],
    companyLogo: "/images/uae-digital-lab-logo.png"
  },
  {
    id: "montiplay-2020",
    company: "Montiplay",
    position: "Software Engineer (Remote)",
    location: "Dubai, United Arab Emirates",
    startDate: "2020-08",
    endDate: "2021-07",
    description: [
      "Built a custom Shopify store using Liquid",
      "Integrated ReCharge for recurring subscriptions",
      "Designed 60+ personalized workflows for tailored toy selection",
      "Connected Node.js APIs with Aramex (shipping) and Airtable (data)",
      "Enabled multilingual support via TranslateLAB"
    ],
    technologies: ["Shopify", "Liquid", "Node.js", "Aramex API", "Airtable", "TranslateLAB"],
    companyLogo: "/images/montiplay-logo.png"
  },
  {
    id: "freelance-2019",
    company: "Freelancer",
    position: "Software Engineer",
    location: "",
    startDate: "2019-01",
    endDate: "2020-08",
    description: [
      "AI Stock – AI-based stock scanning tool",
      "• Built a desktop app using Python and Qt for real-time pipe monitoring",
      "• Integrated YOLO for computer vision to detect inventory from live warehouses and screenshots",
      "Lux Rent – Luxury car rental platform",
      "• Developed the backend using NestJS with TypeScript",
      "• Followed RESTful API design and integrated database persistence"
    ],
    technologies: ["Python", "Qt", "YOLO", "Computer Vision", "NestJS", "TypeScript", "REST API"],
    companyLogo: ""
  }
];

export const projects: Project[] = [
  {
    id: "rta-ai-systems",
    title: "RTA AI Systems - Fourth Industrial Revolution Mobility Lab",
    location: "Dubai, United Arab Emirates",
    link: null,
    description: "AI Proof of Concept (POC) to enhance operational efficiency by integrating artificial intelligence into mobility solutions. Includes AI-powered Occupancy and Demand Prediction, AI-driven Customer Relationship Management, and Voice AI Agent for Trip Planning.",
    role: "Data Scientist",
    technologies: ["AI", ".NET", "Azure", "Clean Architecture", "Microservices", "Python"],
    architecture: "Clean Architecture",
    featured: true,
    image: "/images/rta-ai-preview.jpg"
  },
  {
    id: "sanad-relay",
    title: "SANAD Relay - Community Development Authority",
    location: "Dubai, United Arab Emirates",
    link: "https://sanadrelay.cda.gov.ae/",
    description: "A real-time video communication platform connecting People of Determination (PODs) with interpreters for daily assistance. Includes portals for Administrator, Interpreter, and POD users.",
    role: "Technical Lead",
    technologies: ["Azure", ".NET 8", "Angular", "Flutter", "Python"],
    architecture: "Clean Architecture",
    featured: true,
    image: "/images/sanad-relay-preview.jpg",
    liveUrl: "https://sanadrelay.cda.gov.ae/"
  },
  {
    id: "signvoice",
    title: "Signvoice - Zayed Higher Organization for People of Determination",
    location: "Abu Dhabi, United Arab Emirates",
    link: "https://customer.signvoice.gov.ae/home",
    description: "A live interpreter video platform supporting daily communication needs for PODs.",
    role: "Technical Lead",
    technologies: [".NET 8", "Angular", "Flutter", "Python"],
    architecture: "Clean Architecture",
    featured: true,
    image: "/images/signvoice-preview.jpg",
    liveUrl: "https://customer.signvoice.gov.ae/home"
  },
  {
    id: "dubai-public-parks",
    title: "Dubai Public Parks - Dubai Municipality",
    location: "Dubai, United Arab Emirates",
    link: "https://dubaipublicparks.ae/",
    description: "A unified ticketing platform for all public parks across Dubai, offering a centralized system for booking and managing park entries.",
    role: "Technical Lead",
    technologies: ["Azure", ".NET 8", "Angular", "Flutter"],
    architecture: "Microservices",
    featured: true,
    image: "/images/dubai-parks-preview.jpg",
    liveUrl: "https://dubaipublicparks.ae/"
  },
  {
    id: "masar-dubai-police",
    title: "MASAR - Dubai Police",
    location: "Dubai, United Arab Emirates",
    link: null,
    description: "Sensitive project (details confidential). Designed and implemented Grafana dashboards for system monitoring and analytics. Participated in architecture discussions and backend development.",
    role: "Contributor",
    technologies: ["Liferay DXP"],
    featured: false
  },
  {
    id: "cscs-dfzc",
    title: "CSCS - Dubai Free Zones Council",
    location: "Dubai, United Arab Emirates",
    link: "https://platform.dfzc.ae/",
    description: "A centralized integration platform connecting all free zones in Dubai to streamline license issuance, amendments, and regulatory compliance. Integrated with DET and GDRFA, with over five free zones currently live.",
    role: "System Designer",
    technologies: ["Liferay"],
    featured: true,
    image: "/images/cscs-preview.jpg",
    liveUrl: "https://platform.dfzc.ae/"
  },
  {
    id: "virtual-academy",
    title: "Virtual Academy - Ministry of Industry and Advanced Technology",
    location: "Dubai, United Arab Emirates",
    link: "https://virtualacademy.moiat.gov.ae/",
    description: "A digital training platform serving as a virtual academy with three roles: Ministry Admin, Trainees, and Training Centers.",
    role: "Technical Lead",
    technologies: [".NET 6", "Angular"],
    architecture: "MVC",
    featured: true,
    image: "/images/virtual-academy-preview.jpg",
    liveUrl: "https://virtualacademy.moiat.gov.ae/"
  },
  {
    id: "correspondence-system",
    title: "Correspondence System - Government Media Office",
    location: "Dubai, United Arab Emirates",
    link: null,
    description: "A document and correspondence management system developed to streamline official communications and approvals within the Government Media Office.",
    role: "Technical Lead",
    technologies: [".NET Core 3", "Angular"],
    architecture: "MVC",
    featured: false
  },
  {
    id: "hudhoor",
    title: "Hudhoor - Abu Dhabi Customs",
    location: "Abu Dhabi, United Arab Emirates",
    link: null,
    description: "An internal attendance management system with geo-fencing capabilities, enabling accurate, real-time attendance and location tracking. Portals for HR Administrators and Employees.",
    role: "Technical Lead",
    technologies: [".NET Core 3", "Angular", "React Native", "Python"],
    architecture: "MVC",
    featured: false
  },
  {
    id: "hala-jary",
    title: "Hala Jary",
    location: "Dubai, United Arab Emirates",
    link: null,
    description: "A neighborhood-focused social media app encouraging local engagement and interaction. Includes an Admin Dashboard and Mobile App for users to share events, updates, and resources.",
    role: "Full-Stack Developer",
    technologies: ["Node.js", "React", "Flutter"],
    architecture: "MVC",
    featured: false
  },
  {
    id: "lux-rent-platform",
    title: "Lux Rent - Luxury Car Rental Platform",
    location: "Freelance Project",
    link: null,
    description: "A luxury car rental platform with comprehensive backend system. Developed using NestJS with TypeScript, following RESTful API design principles and integrated database persistence for seamless booking and fleet management.",
    role: "Backend Developer",
    technologies: ["NestJS", "TypeScript", "REST API", "Database Design", "Backend Architecture"],
    architecture: "MVC",
    featured: false,
    image: "/images/lux-rent-preview.jpg"
  },
  {
    id: "ai-stock-tool",
    title: "AI Stock - Intelligent Stock Scanning Tool",
    location: "Freelance Project",
    link: null,
    description: "AI-based stock scanning desktop application for real-time inventory monitoring. Built using Python and Qt with YOLO computer vision integration to detect and analyze inventory from live warehouse feeds and screenshots.",
    role: "AI Developer",
    technologies: ["Python", "Qt", "YOLO", "Computer Vision", "Real-time Processing", "AI"],
    architecture: "Desktop Application",
    featured: false,
    image: "/images/ai-stock-preview.jpg"
  }
];

export const skills: Skill[] = [
  // Programming Languages
  { name: "C#", category: "languages", proficiency: 5, icon: "code" },
  { name: "Python", category: "languages", proficiency: 4, icon: "code" },
  { name: "TypeScript", category: "languages", proficiency: 4, icon: "code" },
  { name: "JavaScript", category: "languages", proficiency: 4, icon: "code" },
  { name: "SQL", category: "languages", proficiency: 4, icon: "database" },
  { name: "C++", category: "languages", proficiency: 3, icon: "code" },

  // Frameworks & Libraries
  { name: ".NET", category: "frameworks", proficiency: 5, icon: "layers" },
  { name: "Angular", category: "frameworks", proficiency: 4, icon: "globe" },
  { name: "Node.js", category: "frameworks", proficiency: 4, icon: "server" },
  { name: "NestJS", category: "frameworks", proficiency: 4, icon: "server" },
  { name: "Flask", category: "frameworks", proficiency: 3, icon: "server" },
  { name: "React Native", category: "frameworks", proficiency: 3, icon: "smartphone" },
  { name: "Flutter", category: "frameworks", proficiency: 3, icon: "smartphone" },

  // Cloud & DevOps
  { name: "Azure", category: "cloud", proficiency: 4, icon: "cloud" },
  { name: "AWS", category: "cloud", proficiency: 3, icon: "cloud" },
  { name: "Docker", category: "cloud", proficiency: 4, icon: "package" },
  { name: "CI/CD", category: "cloud", proficiency: 4, icon: "git-branch" },
  { name: "Redis", category: "cloud", proficiency: 3, icon: "database" },
  { name: "Nginx", category: "cloud", proficiency: 3, icon: "server" },

  // Databases
  { name: "SQL Server", category: "databases", proficiency: 4, icon: "database" },
  { name: "PostgreSQL", category: "databases", proficiency: 4, icon: "database" },
  { name: "MongoDB", category: "databases", proficiency: 3, icon: "database" },
  { name: "Redis", category: "databases", proficiency: 3, icon: "database" },

  // Tools & Others
  { name: "Git", category: "tools", proficiency: 5, icon: "git-branch" },
  { name: "Visual Studio", category: "tools", proficiency: 5, icon: "code" },
  { name: "VS Code", category: "tools", proficiency: 5, icon: "code" },
  { name: "Postman", category: "tools", proficiency: 4, icon: "zap" },
  { name: "Jira", category: "tools", proficiency: 4, icon: "clipboard" },
  { name: "Clean Architecture", category: "tools", proficiency: 5, icon: "layers" },
  { name: "Microservices", category: "tools", proficiency: 4, icon: "grid" },
  { name: "REST APIs", category: "tools", proficiency: 5, icon: "link" },
  { name: "GraphQL", category: "tools", proficiency: 3, icon: "link" }
];

export const education = {
  degree: "Informatics Engineering - Artificial Intelligence Specialty",
  institution: "Arab International University الجامعة العربية الدولية",
  location: "Syria",
  gpa: "3.5",
  maxGpa: "4.0",
  startYear: "2016",
  endYear: "2021"
};

export const achievements = [
  {
    id: "competitive-programmer",
    title: "COMPETITIVE PROGRAMMER",
    description: `Syrian Private Universities Collegiate Programming Contest (SPU-CPC):
    • 1st place (Gold), 2019
    • 2nd place (Silver), 2018  
    • 2nd place tied (Gold), 2020
    
    Participated in the Syrian Collegiate Programming Contest (SCPC): 2018, 2019, 2020`,
    icon: "trophy"
  },
  {
    id: "hackathon-mentor",
    title: "Hackathon Mentor – UAE Hackathon (TDRA, Dubai Municipality, FTA, MBZU) | 2024–2025",
    description: `Mentored participants across multiple UAE Hackathons organized by TDRA (2024), and Dubai Municipality, FTA, and MBZU (2025). Delivered technical guidance through hands-on sessions, helping students and employees develop innovative solutions, overcome real-world problem-solving challenges, and build real-world AI use cases.`,
    icon: "users"
  }
];

export const volunteerWork = [
  {
    id: "competitive-programming-coach",
    title: "Competitive Programming Coach",
    organization: "Arab International University الجامعة العربية الدولية",
    location: "Syria",
    startDate: "2019-11",
    endDate: "2020-05",
    description: `I coached new participants for a semester, teaching key data structures and algorithms to prepare them for competition.
    
    • Vectors, strings, maps, lists, and much more.
    • Binary search, DP, and backtracking.
    • Graphs and tree algorithms.`
  }
];

export const socialLinks = {
  linkedin: "https://ae.linkedin.com/in/nizar-almshantaf-78a0b0203",
  email: "nizar.almshantaf@gmail.com",
  phone: "+971561066821",
  location: "Dubai, United Arab Emirates"
};